import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { githubAuthApiRef } from '@backstage/core-plugin-api';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import {
  createTheme, genPageTheme, lightTheme, darkTheme, shapes
} from '@backstage/theme'
import { ThemeProvider } from '@material-ui/core/styles';
import { default as DarkIcon, default as LightIcon } from '@material-ui/icons/WbSunny';
import CssBaseline from '@material-ui/core/CssBaseline';
import { NewRelicPage } from '@backstage-community/plugin-newrelic';

// const pageThemesFontColorOverride: Record<string, PageTheme> = {};
// Object.keys(defaultPageThemes).map(key => {
//   pageThemesFontColorOverride[key] = {
//     ...defaultPageThemes[key],
//     fontColor: '#FFFFFF',
//   };
// });

const ZapayTheme = createTheme({
  palette: {
    ...lightTheme.palette,
    primary: {
      main: '#fc0464',
    },
    secondary: {
      main: '#f05b8e',
    },
    error: {
      main: '#CC0014',
    },
    warning: {
      main: '#FFDD00',
    },
    info: {
      main: '#00BAFF',
    },
    success: {
      main: '#008449',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#000000',
    },
    banner: {
      info: '#00BAFF',
      error: '#CC0014',
      text: '#000000',
      link: '#002766',
    },
    errorBackground: '#FFFFFF',
    warningBackground: '#FFFFFF',
    infoBackground: '#FFFFFF',
    navigation: {
      background: '#fc0464',
      indicator: '#FFFFFF',
      navItem: {
        hoverBackground: '#0c4499',
      },
      color: '#FFFFFF',
      selectedColor: '#FFFFFF',
    },
  },
  defaultPageTheme: 'home',
  // fontFamily: 'Comic Sans MS',
  /* below drives the header colors */
  pageTheme: {
    home: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.round }),
    service: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave,
    }),
    other: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
  },
});

const ZapayDarkTheme = createTheme({
  palette: {
    ...darkTheme.palette,
    primary: {
      main: '#fc0464',
    },
    secondary: {
      main: '#f05b8e',
    },
    navigation: {
      background: '#fc0464',
      indicator: '#FFFFFF',
      navItem: {
        hoverBackground: '#CCCCCC',
      },
      color: '#FFFFFF',
      selectedColor: '#FFFFFF',
    },
  },
  defaultPageTheme: 'home',
  // fontFamily: 'Comic Sans MS',
  /* below drives the header colors */
  pageTheme: {
    home: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
    documentation: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.round }),
    service: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: ['#fc0464', '#fc0464'],
      shape: shapes.wave,
    }),
    other: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
    app: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
    apis: genPageTheme({ colors: ['#fc0464', '#fc0464'], shape: shapes.wave }),
  },
});

const app = createApp({
  themes: [{
    id: 'zapay-theme',
    title: 'Zapay',
    variant: 'light',
    icon: <LightIcon />,
    Provider: ({ children }) => (
      <ThemeProvider theme={ZapayTheme}>
        <CssBaseline>{children}</CssBaseline>
      </ThemeProvider>
    ),
  },
  {
    id: 'ZapayDarkTheme',
    title: 'Dark Theme',
    variant: 'dark',
    icon: <DarkIcon />,
    Provider: ({ children }) => (
      <ThemeProvider theme={ZapayDarkTheme}>
        <CssBaseline>{children}</CssBaseline>
      </ThemeProvider>
    ),
  }],
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'github-auth-provider',
          title: 'GitHub',
          message: 'Sign in using GitHub',
          apiRef: githubAuthApiRef,
        }}
      />
    ),
  },
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/newrelic" element={<NewRelicPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
